import { useCallback, useEffect, useMemo, useState } from 'react'
import { Card } from '@pilot-hub/ui'
import { flightApi } from '@pilot-hub/api-client'
import { Users, Check, AlertTriangle, Loader2 } from 'lucide-react'
import { useAuth } from '@pilot-hub/auth'
import { useEinsatzTrupps } from '../hooks/useEinsatzTrupps'
import { TruppUebernehmen } from './TruppUebernehmen'

export interface CrewFlug {
  id: number
  flug_nr?: string | null
  drohne_label?: string | null
  pilot_id?: number | null
  co_pilot_id?: number | null
  beobachter_id?: number | null
  beobachter_label?: string | null
}

interface Person { id: number; name: string; funktionen: string[] }

type Feld = 'pilot_id' | 'co_pilot_id' | 'beobachter_id'
const FELDER: { feld: Feld; label: string; funktion: string }[] = [
  { feld: 'pilot_id', label: 'Pilot', funktion: 'PILOT' },
  { feld: 'co_pilot_id', label: 'Co-Pilot', funktion: 'CO_PILOT' },
  { feld: 'beobachter_id', label: 'Luftraumbeobachter', funktion: 'LUFTRAUM_BEOBACHTER' },
]

/**
 * Besetzung des aktiven Flugs: Pilot, Co-Pilot, Luftraumbeobachter. Auswahl = Einsatzkräfte des Einsatzes
 * (Kräfte Management) mit passender Funktion, gespeichert wird sofort bei der Auswahl (PUT /fluege/{id}).
 */
export function FlugCrewKarte({ einsatzId, fluege, onGeaendert }: { einsatzId: number | null; fluege: CrewFlug[]; onGeaendert?: () => void }) {
  const [werte, setWerte] = useState<Record<number, Partial<Record<Feld, number | null>>>>({})
  const [status, setStatus] = useState<Record<string, 'speichert' | 'ok' | 'fehler'>>({})
  const [meldung, setMeldung] = useState<string | null>(null)
  const [flugId, setFlugId] = useState<number | null>(null)

  const aktiv = useMemo(() => fluege.find(f => f.id === flugId) ?? fluege[0] ?? null, [fluege, flugId])

  const { user } = useAuth()
  const { trupps, personen: kraefte, nutzerListe } = useEinsatzTrupps(einsatzId)
  const meinTrupp = trupps.find(t => user && t.mitglieder.includes(Number((user as any).id)))
  // Einsatzkräfte mit ihren Funktionen (Kräfte Management inkl. Reserve und Trupps)
  const personen: Person[] = useMemo(() => [...kraefte.values()].map(p => ({ id: p.id, name: p.name, funktionen: p.funktionen })).sort((a, b) => a.name.localeCompare(b.name, 'de')), [kraefte])

  const wert = (f: CrewFlug, feld: Feld) => (werte[f.id]?.[feld] !== undefined ? werte[f.id][feld] : f[feld]) ?? null

  const speichern = useCallback(async (f: CrewFlug, feld: Feld, neu: number | null) => {
    const key = `${f.id}:${feld}`
    const alt = wert(f, feld)
    setWerte(w => ({ ...w, [f.id]: { ...w[f.id], [feld]: neu } }))
    setStatus(s => ({ ...s, [key]: 'speichert' }))
    setMeldung(null)
    try {
      await flightApi.update(f.id, { [feld]: neu })
      setStatus(s => ({ ...s, [key]: 'ok' }))
      setMeldung('Gespeichert.')
      onGeaendert?.()
      window.setTimeout(() => setStatus(s => (s[key] === 'ok' ? { ...s, [key]: undefined as any } : s)), 2500)
    } catch (e: any) {
      setWerte(w => ({ ...w, [f.id]: { ...w[f.id], [feld]: alt } }))
      setStatus(s => ({ ...s, [key]: 'fehler' }))
      setMeldung(e?.response?.data?.detail || 'Speichern fehlgeschlagen – bitte erneut versuchen.')
    }
  }, [werte, onGeaendert]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card className="p-4" data-testid="flug-crew-karte">
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <Users className="h-5 w-5 shrink-0 text-sky-400" />
        <h3 className="font-semibold text-dark-text">Besetzung des aktiven Flugs</h3>
        {aktiv && <span className="min-w-0 break-words text-sm text-dark-text-muted">{aktiv.flug_nr ? `Flug ${aktiv.flug_nr}` : `Flug ${aktiv.id}`}{aktiv.drohne_label ? ` · ${aktiv.drohne_label}` : ''}</span>}
        {aktiv && trupps.length > 0 && (
          <div className="sm:ml-auto">
            <TruppUebernehmen einsatzId={einsatzId} flug={aktiv} trupps={trupps} personen={kraefte} nutzerListe={nutzerListe}
              vorgabeTruppKey={meinTrupp?.key ?? null} onFertig={v => { setWerte(w => ({ ...w, [aktiv.id]: v })); setMeldung('Trupp übernommen.'); onGeaendert?.() }} />
          </div>
        )}
        {fluege.length > 1 && (
          <select aria-label="Flug wählen" value={aktiv?.id ?? ''} onChange={e => setFlugId(Number(e.target.value))}
            className="min-h-[44px] rounded-lg border border-dark-border bg-dark-bg-tertiary px-3 text-sm text-dark-text">
            {fluege.map(f => <option key={f.id} value={f.id}>{f.flug_nr ? `Flug ${f.flug_nr}` : `Flug ${f.id}`}{f.drohne_label ? ` · ${f.drohne_label}` : ''}</option>)}
          </select>
        )}
      </div>

      {!aktiv ? (
        <p className="py-2 text-dark-text-muted" data-testid="crew-kein-flug">Kein aktiver Flug</p>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {FELDER.map(({ feld, label, funktion }) => {
              const st = status[`${aktiv.id}:${feld}`]
              const v = wert(aktiv, feld)
              const passend = personen.filter(p => p.funktionen.includes(funktion))
              // gespeicherter Altwert ohne passende Funktion: weiter angezeigt (markiert), aber nicht neu wählbar
              const alt = v != null && !passend.some(p => p.id === v) ? { id: v, name: nutzerListe.get(v)?.name ?? personen.find(p => p.id === v)?.name ?? `Benutzer ${v}`, passt: (nutzerListe.get(v)?.funktionen ?? []).includes(funktion) } : null
              return (
                <label key={feld} className="block rounded-lg bg-dark-bg-tertiary/50 p-3">
                  <span className="flex items-center justify-between gap-2 text-xs text-dark-text-muted">
                    <span>{label}</span>
                    {st === 'speichert' && <Loader2 className="h-4 w-4 animate-spin" />}
                    {st === 'ok' && <Check className="h-4 w-4 text-green-400" />}
                    {st === 'fehler' && <AlertTriangle className="h-4 w-4 text-red-400" />}
                  </span>
                  <select data-testid={`crew-${feld}`} value={v ?? ''} disabled={st === 'speichert'}
                    onChange={e => speichern(aktiv, feld, e.target.value ? Number(e.target.value) : null)}
                    className="mt-1 min-h-[44px] w-full rounded-lg border border-dark-border bg-dark-bg-tertiary px-3 text-base font-semibold text-dark-text">
                    <option value="">– nicht besetzt –</option>
                    {alt && <option value={alt.id} disabled={!alt.passt}>{alt.name}{alt.passt ? '' : ` (Funktion ${label} fehlt)`}</option>}
                    {passend.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </label>
              )
            })}
          </div>
          {meldung && <p role="status" className={`mt-2 text-sm ${meldung === 'Gespeichert.' || meldung === 'Trupp übernommen.' ? 'text-green-400' : 'text-red-300'}`} data-testid="crew-meldung">{meldung}</p>}
        </>
      )}
    </Card>
  )
}
